/**
 * Location autocomplete (items 13-15). Searches the places source as you type; picking a known spot
 * auto-fills the neighborhood (and prompts "Which one?" for multi-location spots). "+ Add a new
 * spot" first fuzzy-matches to catch typos/near-duplicates ("Did you mean …?") and only accepts a
 * genuinely new name as a pending spot. Free-typed text still sets the name (so a review is never
 * blocked on the place list).
 */
import { useEffect, useRef, useState } from 'react';
import type { Place } from '@pghbites/shared';
import { searchPlaces, addNewPlace } from '../api.js';

interface PlacePickerProps {
  value: string;
  onName: (name: string) => void;
  onSelect: (name: string, neighborhood: string, placeId: string | undefined, isNew: boolean) => void;
}

export function PlacePicker({ value, onName, onSelect }: PlacePickerProps) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<Place[]>([]);
  const [open, setOpen] = useState(false);
  const [multi, setMulti] = useState<Place | null>(null);
  const [suggest, setSuggest] = useState<Place | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search — only while the dropdown is open (no fetch on mount / when unfocused).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const places = await searchPlaces(query.trim());
      if (!cancelled) setResults(places);
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, open]);

  const finish = (name: string, neighborhood: string, placeId: string | undefined, isNew: boolean) => {
    onSelect(name, neighborhood, placeId, isNew);
    setQuery(name);
    setOpen(false);
    setMulti(null);
    setSuggest(null);
    setError(null);
  };

  const onResultClick = (p: Place) => {
    if (p.neighborhoods.length > 1) {
      setMulti(p);
    } else {
      finish(p.name, p.neighborhoods[0] ?? '', p.id, p.status === 'pending');
    }
  };

  const onAddNew = async () => {
    const name = query.trim();
    if (!name) return;
    setAdding(true);
    setError(null);
    const res = await addNewPlace(name);
    setAdding(false);
    if (res.suggestion) {
      setSuggest(res.suggestion);
    } else if (res.ok && res.place) {
      finish(res.place.name, '', res.place.id, true);
    } else {
      setError(res.message ?? 'Could not add that spot.');
    }
  };

  const onAddAnyway = async () => {
    const name = query.trim();
    setAdding(true);
    const res = await addNewPlace(name, undefined, true);
    setAdding(false);
    if (res.ok && res.place) finish(res.place.name, '', res.place.id, true);
    else setError(res.message ?? 'Could not add that spot.');
  };

  const keepOpen = (e: React.MouseEvent) => e.preventDefault(); // don't blur-close before click

  return (
    <div className="place-picker">
      <label className="field" htmlFor="restaurant">
        <span className="field-label">
          Which spot are you reviewing?<span className="req" aria-hidden="true"> *</span>
        </span>
        <input
          id="restaurant"
          className="field-input"
          type="text"
          value={query}
          placeholder="Search a Pittsburgh spot…"
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            onName(e.target.value);
            setOpen(true);
            setMulti(null);
            setSuggest(null);
            setError(null);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 160);
          }}
        />
      </label>

      {open && (
        <div className="place-dropdown" onMouseDown={keepOpen}>
          {multi ? (
            <div className="place-multi">
              <p className="place-multi-q">Which {multi.name}?</p>
              {multi.neighborhoods.map((nb) => (
                <button key={nb} type="button" className="place-option" onClick={() => finish(multi.name, nb, multi.id, false)}>
                  {multi.name} <span className="place-nb">{nb}</span>
                </button>
              ))}
            </div>
          ) : suggest ? (
            <div className="place-suggest">
              <p>
                Did you mean <strong>{suggest.name}</strong>
                {suggest.neighborhoods[0] ? ` (${suggest.neighborhoods[0]})` : ''}?
              </p>
              <div className="place-suggest-actions">
                <button type="button" className="chip" onClick={() => onResultClick(suggest)}>
                  Yes, that one
                </button>
                <button type="button" className="chip" onClick={onAddAnyway} disabled={adding}>
                  No, it’s new
                </button>
              </div>
            </div>
          ) : (
            <>
              {results.map((p) => (
                <button key={p.id} type="button" className="place-option" onClick={() => onResultClick(p)}>
                  {p.name}{' '}
                  <span className="place-nb">
                    {p.neighborhoods.length > 1 ? `${p.neighborhoods.length} locations` : p.neighborhoods[0] ?? ''}
                  </span>
                </button>
              ))}
              {query.trim() && (
                <button type="button" className="place-add" onClick={onAddNew} disabled={adding}>
                  ➕ Add a new spot{query.trim() ? `: “${query.trim()}”` : ''}
                </button>
              )}
              {error && (
                <p className="add-own-error" role="alert">
                  {error}
                </p>
              )}
            </>
          )}
          {/* Required attribution wherever Google place data is shown (legal item 8). */}
          <p className="place-attribution">Place data ©&nbsp;Google Maps</p>
        </div>
      )}
    </div>
  );
}
